import { useEffect } from "react";
import "./styles/theme.css";
import "./styles/formControls.css";
import "./styles/buttons.css";
import "./styles/dialog.css";
import { Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AppProvider } from "./context/AppContext";
import { UIProvider } from "./context/UIContext";
import { MeetingConfigProvider } from "./context/MeetingConfigContext";
import { ReportConfigProvider } from "./context/ReportConfigContext";
import { NotificationProvider } from "./context/NotificationContext";
import { ChatProvider } from "./context/ChatContext";
import { ROUTES, IS_DEV } from "./constants/constants";
import ErrorBoundary from "./components/common/ErrorBoundary";
import errorReporter from "./utils/errorReporter";

// Pages
import LoginPage from "./pages/LoginPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import LegalPage from "./pages/LegalPage";
import McaFormPreparePage from "./pages/McaFormPreparePage";
import MagicLogin from "./pages/MagicLogin";
import AcceptInvitationPage from "./pages/AcceptInvitationPage";
import UpsiNoticeAckPage from "./pages/UpsiNoticeAckPage";
import DashboardProPage from "./pages/DashboardProPage";
import CompaniesPage from "./pages/CompaniesPage";
import AlertsPage from "./pages/AlertsPage";
import CompliancePage from "./pages/CompliancePage";
// Company Types
import CompanyTypesPage from "./pages/CompanyTypesPage";
import PvtLimitedPage from "./pages/PvtLimitedPage";
import LLPPage from "./pages/LLPPage";
import OPCPage from "./pages/OPCPage";
import PublicCoPage from "./pages/PublicCoPage";
import NGOPage from "./pages/NGOPage";
// AI & Documents
import AgentPage from "./pages/AgentPage";
import MinutesPage from "./pages/MinutesPage";
import MeetingsPage from "./pages/MeetingsPage";
import ResolutionsPage from "./pages/ResolutionsPage";
import DocumentLibraryPage from "./pages/DocumentLibraryPage";
import MyDocumentsPage from "./pages/MyDocumentsPage";
import MyTemplatesPage from "./pages/MyTemplatesPage";
// Collaborator Management
import ManageCollaborators from "./pages/ManageCollaborators";
import AssignmentActivityPage from "./pages/AssignmentActivityPage";
import ProfessionalDashboard from "./pages/ProfessionalDashboard";
// Filing & Calendar
import AnnualFilingPage from "./pages/AnnualFilingPage";
import AGMCalculatorPage from "./pages/AGMCalculatorPage";
import CalendarPage from "./pages/CalendarPage";
import MCAFormsPage from "./pages/MCAFormsPage";
// SEBI PIT — Structured Digital Database
import RegistersPage from "./pages/RegistersPage";
import SddPage from "./pages/SddPage";
import ReportTemplatesPage from "./pages/ReportTemplatesPage";
import ReportGeneratorPage from "./pages/ReportGeneratorPage";
// Business Platform (Phase 2 — dev-only, gated by IS_DEV)
import MarketplacePage from "./pages/MarketplacePage";
import ValuationPage from "./pages/ValuationPage";
import DueDiligencePage from "./pages/DueDiligencePage";
// Privacy & Security
import PrivacyPage from "./pages/PrivacyPage";
import DPControlsPage from "./pages/DPControlsPage";
// Account
import PackagesPage from "./pages/PackagesPage";
import ProfilePage from "./pages/ProfilePage";
// Admin
import AdminDashboardPage from "./pages/AdminDashboardPage";
import AIControlPlanePage from "./pages/AIControlPlanePage";
import AdminUsersPage from "./pages/AdminUsersPage";
import AdminSddMonitoringPage from "./pages/AdminSddMonitoringPage";
// Support
import SupportPage from "./pages/SupportPage";
import SupportQueuePage from "./pages/SupportQueuePage";
import AdminAlertsPage from "./pages/AdminAlertsPage";
import AdminEntitlementsPage from "./pages/AdminEntitlementsPage";
import AdminComplianceRulesPage from "./pages/AdminComplianceRulesPage";
import MyAiUsagePage from "./pages/MyAiUsagePage";
// Dev-only: living design-system reference
import DesignSystemPage from "./pages/DesignSystemPage";

const NEW_UI_BASE_PATH = "/new-ui";
const childPath = (absolutePath) => {
  if (typeof absolutePath !== "string") {
    console.error("[childPath] received non-string:", absolutePath);
    return "";
  }
  return absolutePath.replace(`${NEW_UI_BASE_PATH}/`, "");
};

/** Themed loading spinner shown during auth check */
function AuthLoadingScreen() {
  return (
    <div
      className="h-[var(--app-vh)] w-full flex items-center justify-center"
      style={{ background: "#f4f0e8" }}
    >
      <div className="text-center px-8">
        <img
          src="/complyv-logo.svg"
          alt=""
          className="w-[52px] h-[52px] mx-auto mb-4 object-contain"
        />
        <div
          className="text-[28px] font-bold leading-none mb-2"
          style={{
            fontFamily: "'Cormorant Garamond', serif",
            color: "#b8973a",
            letterSpacing: ".3px",
          }}
        >
          ComplyV
        </div>
        <div
          className="text-[8.5px] tracking-[2.8px] uppercase mb-7"
          style={{ color: "rgba(122,112,96,.65)" }}
        >
          One-Stop Business &amp; Compliance
        </div>
        <div
          className="w-[200px] h-[2px] mx-auto rounded-full overflow-hidden relative"
          style={{ background: "rgba(184,151,58,.15)" }}
        >
          <div
            className="absolute top-0 bottom-0 rounded-full cs-loading-bar"
            style={{
              width: "40%",
              background:
                "linear-gradient(90deg, transparent 0%, #b8973a 45%, #1e8a8a 75%, transparent 100%)",
            }}
          />
        </div>
      </div>
      <style>{`
        @keyframes cs-loading-slide {
          0% { left: -45%; }
          100% { left: 100%; }
        }
        .cs-loading-bar {
          animation: cs-loading-slide 1.6s cubic-bezier(.4,0,.2,1) infinite;
        }
      `}</style>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <AuthLoadingScreen />;
  if (!isAuthenticated) return <Navigate to={ROUTES.LOGIN} replace />;

  // Redirect admin users to admin dashboard
  const roleName = String(user?.roleName || "").toLowerCase();
  if (["admin", "super_admin"].includes(roleName)) {
    return <Navigate to={ROUTES.ADMIN_DASHBOARD} replace />;
  }

  return children;
}

// Any authenticated user, regardless of role. Unlike ProtectedRoute (which
// bounces admins to the admin dashboard), this renders for every signed-in
// user type — used for the role-aware profile page, which is intentionally
// shared by professionals, corporates, and admins alike.
function AuthedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <AuthLoadingScreen />;
  if (!isAuthenticated) return <Navigate to={ROUTES.LOGIN} replace />;
  return children;
}

function AuthRoute({ children }) {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) return <AuthLoadingScreen />;
  if (isAuthenticated) {
    // Redirect admin users to admin dashboard, others to regular dashboard
    const roleName = String(user?.roleName || "").toLowerCase();
    return (
      <Navigate
        to={
          ["admin", "super_admin"].includes(roleName)
            ? ROUTES.ADMIN_DASHBOARD
            : ROUTES.DASHBOARD
        }
        replace
      />
    );
  }
  return children;
}

// /design-system: any signed-in user in dev builds; super_admin only in prod
// (it's an internal reference, not a customer surface — but super admins may
// need it live to review the design language against production).
function DesignSystemRoute({ children }) {
  const { user, isAuthenticated, loading } = useAuth();
  if (loading) return <AuthLoadingScreen />;
  if (!isAuthenticated) return <Navigate to={ROUTES.LOGIN} replace />;
  const roleName = String(user?.roleName || "").toLowerCase();
  if (!IS_DEV && roleName !== "super_admin") {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }
  return children;
}

// Client defense-in-depth for super_admin-only surfaces (Flag Inbox, Admin
// Alerts). The backend already enforces superAdminOnly; this keeps plain
// admins from landing on a page that would just 403.
function SuperAdminRoute({ children }) {
  const { user, isAuthenticated, loading } = useAuth();
  if (loading) return <AuthLoadingScreen />;
  if (!isAuthenticated) return <Navigate to={ROUTES.LOGIN} replace />;
  const roleName = String(user?.roleName || "").toLowerCase();
  if (roleName !== "super_admin") {
    return <Navigate to={ROUTES.ADMIN_DASHBOARD} replace />;
  }
  return children;
}

function AdminRoute({ children }) {
  const { user, isAuthenticated, loading } = useAuth();
  if (loading) return <AuthLoadingScreen />;
  if (!isAuthenticated) return <Navigate to={ROUTES.LOGIN} replace />;
  const roleName = String(user?.roleName || "").toLowerCase();
  if (!["admin", "super_admin"].includes(roleName)) {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }
  return children;
}

function CorporateRoute({ children }) {
  const { user, isAuthenticated, loading } = useAuth();
  if (loading) return <AuthLoadingScreen />;
  if (!isAuthenticated) return <Navigate to={ROUTES.LOGIN} replace />;
  const roleName = String(user?.roleName || "").toLowerCase();
  if (roleName !== "corporate") {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }
  return children;
}

function ProfessionalRoute({ children }) {
  const { user, isAuthenticated, loading } = useAuth();
  if (loading) return <AuthLoadingScreen />;
  if (!isAuthenticated) return <Navigate to={ROUTES.LOGIN} replace />;
  const roleName = String(user?.roleName || "").toLowerCase();
  if (roleName !== "professional") {
    return <Navigate to={ROUTES.DASHBOARD} replace />;
  }
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route
        path={childPath(ROUTES.ACCEPT_INVITATION)}
        element={<AcceptInvitationPage />}
      />
      {/* Reg 3(2B) acknowledgement — reached from an email by people with no
          account here, so it must stay outside every auth wrapper. */}
      <Route
        path={childPath(ROUTES.SDD_NOTICE_ACK)}
        element={<UpsiNoticeAckPage />}
      />
      {/* Terms, privacy, refunds, contact. Outside every auth wrapper: a
          payment gateway reviews them logged out, and so does anyone
          deciding whether to trust us with their register. */}
      <Route
        path={childPath(ROUTES.LEGAL)}
        element={<LegalPage />}
      />

      {/* Auth */}
      <Route
        path={childPath(ROUTES.LOGIN)}
        element={
          <AuthRoute>
            <LoginPage />
          </AuthRoute>
        }
      />
      {/* Reset password — token validated on the page itself, no auth wrapper
          (a logged-in user clicking an emailed reset link should still work). */}
      <Route
        path={childPath(ROUTES.RESET_PASSWORD)}
        element={<ResetPasswordPage />}
      />
      {/* Magic-link sign-in — token consumed via interstitial confirm button.
          No auth wrapper (the whole point is to log unauthenticated users in). */}
      <Route path={childPath(ROUTES.MAGIC_LOGIN)} element={<MagicLogin />} />

      {/* Overview */}
      <Route
        path={childPath(ROUTES.DASHBOARD)}
        element={
          <ProtectedRoute>
            <DashboardProPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={childPath(ROUTES.COMPANIES)}
        element={
          <ProtectedRoute>
            <CompaniesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={childPath(ROUTES.ALERTS)}
        element={
          <ProtectedRoute>
            <AlertsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={childPath(ROUTES.COMPLIANCE)}
        element={
          <ProtectedRoute>
            <CompliancePage />
          </ProtectedRoute>
        }
      />

      {/* Company Types */}
      <Route
        path={childPath(ROUTES.COMPANY_TYPES)}
        element={
          <ProtectedRoute>
            <CompanyTypesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={childPath(ROUTES.PVT_LIMITED)}
        element={
          <ProtectedRoute>
            <PvtLimitedPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={childPath(ROUTES.LLP)}
        element={
          <ProtectedRoute>
            <LLPPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={childPath(ROUTES.OPC)}
        element={
          <ProtectedRoute>
            <OPCPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={childPath(ROUTES.PUBLIC_CO)}
        element={
          <ProtectedRoute>
            <PublicCoPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={childPath(ROUTES.NGO)}
        element={
          <ProtectedRoute>
            <NGOPage />
          </ProtectedRoute>
        }
      />

      {/* AI & Documents — AI Agent is live for professional/corporate users */}
      <Route
        path={`${childPath(ROUTES.AGENT)}/*`}
        element={
          <ProtectedRoute>
            <AgentPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={childPath(ROUTES.MINUTES)}
        element={
          <ProtectedRoute>
            <MinutesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={childPath(ROUTES.RESOLUTIONS)}
        element={
          <ProtectedRoute>
            <ResolutionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={childPath(ROUTES.DOC_LIBRARY)}
        element={
          <ProtectedRoute>
            <DocumentLibraryPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={childPath(ROUTES.MY_TEMPLATES)}
        element={
          <ProtectedRoute>
            <MyTemplatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={childPath(ROUTES.MY_DOCUMENTS)}
        element={
          <ProtectedRoute>
            <MyDocumentsPage />
          </ProtectedRoute>
        }
      />

      {/* Collaborator Management */}
      <Route
        path={childPath(ROUTES.MANAGE_COLLABORATORS)}
        element={
          <CorporateRoute>
            <ManageCollaborators />
          </CorporateRoute>
        }
      />
      <Route
        path={`${childPath(ROUTES.MANAGE_COLLABORATORS)}/:assignmentId/activity`}
        element={
          <CorporateRoute>
            <AssignmentActivityPage />
          </CorporateRoute>
        }
      />
      <Route
        path={childPath(ROUTES.PROFESSIONAL_DASHBOARD)}
        element={
          <ProfessionalRoute>
            <ProfessionalDashboard />
          </ProfessionalRoute>
        }
      />

      {/* Filing & Calendar */}
      <Route
        path={childPath(ROUTES.ANNUAL_FILING)}
        element={
          <ProtectedRoute>
            <AnnualFilingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={childPath(ROUTES.AGM_CALC)}
        element={
          <ProtectedRoute>
            <AGMCalculatorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={childPath(ROUTES.CALENDAR)}
        element={
          <ProtectedRoute>
            <CalendarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={childPath(ROUTES.MCA_FORMS)}
        element={
          <ProtectedRoute>
            <MCAFormsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={childPath(ROUTES.MCA_PREPARE)}
        element={
          <ProtectedRoute>
            <McaFormPreparePage />
          </ProtectedRoute>
        }
      />
      <Route
        path={childPath(ROUTES.MEETINGS)}
        element={
          <ProtectedRoute>
            <MeetingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={childPath(ROUTES.REGISTERS)}
        element={
          <ProtectedRoute>
            <RegistersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={childPath(ROUTES.SDD)}
        element={
          <ProtectedRoute>
            <SddPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={childPath(ROUTES.REPORT_TEMPLATES)}
        element={
          <ProtectedRoute>
            <ReportTemplatesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={childPath(ROUTES.REPORT_GENERATOR)}
        element={
          <ProtectedRoute>
            <ReportGeneratorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="reports"
        element={<Navigate to="report-templates" replace />}
      />

      {/* Business Platform (Phase 2 — dev-only) */}
      {IS_DEV && (
        <Route
          path={childPath(ROUTES.MARKETPLACE)}
          element={
            <ProtectedRoute>
              <MarketplacePage />
            </ProtectedRoute>
          }
        />
      )}
      {IS_DEV && (
        <Route
          path={childPath(ROUTES.VALUATION)}
          element={
            <ProtectedRoute>
              <ValuationPage />
            </ProtectedRoute>
          }
        />
      )}
      {IS_DEV && (
        <Route
          path={childPath(ROUTES.DUE_DILIGENCE)}
          element={
            <ProtectedRoute>
              <DueDiligencePage />
            </ProtectedRoute>
          }
        />
      )}

      {/* Privacy & Security */}
      <Route
        path={childPath(ROUTES.PRIVACY)}
        element={
          <ProtectedRoute>
            <PrivacyPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={childPath(ROUTES.DP_CONTROLS)}
        element={
          <ProtectedRoute>
            <DPControlsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path={childPath(ROUTES.MY_AI_USAGE)}
        element={
          <ProtectedRoute>
            <MyAiUsagePage />
          </ProtectedRoute>
        }
      />

      {/* Account */}
      <Route
        path={childPath(ROUTES.PROFILE)}
        element={
          <AuthedRoute>
            <ProfilePage />
          </AuthedRoute>
        }
      />

      {/* Support */}
      <Route
        path={childPath(ROUTES.SUPPORT)}
        element={
          <AuthedRoute>
            <SupportPage />
          </AuthedRoute>
        }
      />
      <Route
        path={childPath(ROUTES.PACKAGES)}
        element={
          <ProtectedRoute>
            <PackagesPage />
          </ProtectedRoute>
        }
      />

      {/* Admin (requires admin or super_admin role) */}
      <Route
        path={childPath(ROUTES.ADMIN_DASHBOARD)}
        element={
          <AdminRoute>
            <AdminDashboardPage />
          </AdminRoute>
        }
      />
      <Route
        path={childPath(ROUTES.ADMIN_USERS)}
        element={
          <AdminRoute>
            <AdminUsersPage />
          </AdminRoute>
        }
      />
      <Route
        path={childPath(ROUTES.ADMIN_SDD)}
        element={
          <AdminRoute>
            <AdminSddMonitoringPage />
          </AdminRoute>
        }
      />

      {/* Additional Admin Routes (all handled within AdminDashboardPage with tab navigation) */}
      <Route
        path={childPath("/admin/services")}
        element={
          <AdminRoute>
            <AdminDashboardPage />
          </AdminRoute>
        }
      />
      {/* Legacy AI routes — retired into the dedicated AI Control Plane section. */}
      <Route
        path={childPath("/admin/ai-models")}
        element={
          <AdminRoute>
            <Navigate to={ROUTES.ADMIN_AI_PROVIDERS} replace />
          </AdminRoute>
        }
      />
      <Route
        path={childPath("/admin/ai-usage")}
        element={
          <AdminRoute>
            <Navigate to={ROUTES.ADMIN_AI_USAGE_COST} replace />
          </AdminRoute>
        }
      />
      <Route
        path={childPath("/admin/ai")}
        element={
          <AdminRoute>
            <Navigate to={ROUTES.ADMIN_AI_OVERVIEW} replace />
          </AdminRoute>
        }
      />
      <Route
        path={childPath("/admin/ai/:screen")}
        element={
          <AdminRoute>
            <AIControlPlanePage />
          </AdminRoute>
        }
      />
      <Route
        path={childPath("/admin/roles")}
        element={
          <AdminRoute>
            <AdminDashboardPage />
          </AdminRoute>
        }
      />
      <Route
        path={childPath("/admin/companies")}
        element={
          <AdminRoute>
            <AdminDashboardPage />
          </AdminRoute>
        }
      />
      <Route
        path={childPath("/admin/orders")}
        element={
          <AdminRoute>
            <AdminDashboardPage />
          </AdminRoute>
        }
      />
      <Route
        path={childPath("/admin/audit-logs")}
        element={
          <AdminRoute>
            <AdminDashboardPage />
          </AdminRoute>
        }
      />
      <Route
        path={childPath("/admin/error-logs")}
        element={
          <AdminRoute>
            <AdminDashboardPage />
          </AdminRoute>
        }
      />
      <Route
        path={childPath("/admin/flag-inbox")}
        element={
          <SuperAdminRoute>
            <AdminDashboardPage />
          </SuperAdminRoute>
        }
      />
      <Route
        path={childPath(ROUTES.ADMIN_ALERTS_FEED)}
        element={
          <SuperAdminRoute>
            <AdminAlertsPage />
          </SuperAdminRoute>
        }
      />
      <Route
        path={childPath(ROUTES.ADMIN_ENTITLEMENTS)}
        element={
          <AdminRoute>
            <AdminEntitlementsPage />
          </AdminRoute>
        }
      />
      <Route
        path={childPath(ROUTES.ADMIN_COMPLIANCE_RULES)}
        element={
          <AdminRoute>
            <AdminComplianceRulesPage />
          </AdminRoute>
        }
      />
      <Route
        path={childPath(ROUTES.ADMIN_SUPPORT_QUEUE)}
        element={
          <AdminRoute>
            <SupportQueuePage />
          </AdminRoute>
        }
      />

      {/* Living design-system reference — all authed users in dev,
          super_admin only in production. */}
      <Route
        path={childPath(ROUTES.DESIGN_SYSTEM)}
        element={
          <DesignSystemRoute>
            <DesignSystemPage />
          </DesignSystemRoute>
        }
      />

      {/* Fallbacks */}
      <Route
        path="/"
        element={<Navigate to={childPath(ROUTES.DASHBOARD)} replace />}
      />
      <Route path="*" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
    </Routes>
  );
}

const NewUIApp = () => {
  // Prevent backspace from triggering browser back navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key !== "Backspace") return;
      const el = document.activeElement;
      if (!el) return;
      const tag = el.tagName.toLowerCase();
      // Allow backspace in all form inputs, textareas, selects, and contentEditable elements
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (el.isContentEditable) return;
      e.preventDefault();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  // Install global error/unhandledrejection hooks once. They ship browser-side
  // crashes to POST /api/errors/report so they show up in Admin → Error Logs.
  // ErrorBoundary below also reports React render errors.
  useEffect(() => {
    errorReporter.install();
  }, []);

  return (
    <ErrorBoundary>
      <AuthProvider>
        <AppProvider>
          <MeetingConfigProvider>
            <ReportConfigProvider>
              <UIProvider>
                <NotificationProvider>
                  {/* Above <AppRoutes> on purpose: the chat conversation must
                      outlive page navigation so a generation keeps streaming when
                      the user leaves /agent and comes back (see ChatContext). */}
                  <ChatProvider>
                    <AppRoutes />
                  </ChatProvider>
                </NotificationProvider>
              </UIProvider>
            </ReportConfigProvider>
          </MeetingConfigProvider>
        </AppProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default NewUIApp;
